import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Plus, MessageSquare, Users, FileText, FileEdit, Package, MapPin, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Job, PublicRFQCriteria } from '@/lib/types/job';
import { BidsTab } from './BidsTab';
import { MessagesTab } from './MessagesTab';
import { useJobSpecifications, useRFQSpecificationQuantities } from '@/hooks/useJobSpecifications';
import { RFQVisibilitySelector } from '@/components/rfq/RFQVisibilitySelector';
import { VendorInvitationManager } from '@/components/rfq/VendorInvitationManager';
import { PublicRFQCriteriaBuilder, PublicRFQCriteriaBuilderRef } from '@/components/rfq/PublicRFQCriteriaBuilder';
import { ShippingTermsSelector } from '@/components/rfq/ShippingTermsSelector';
import { useAuth } from '@/lib/auth';
import { SpecificationDataRenderer } from '@/components/specifications/SpecificationDataRenderer';
import { usePublicRFQCriteria } from '@/hooks/usePublicRFQCriteria';
import { QuantitiesMatrix } from './QuantitiesMatrix';
import { QuantitiesMatrixDirect } from './QuantitiesMatrixDirect';
import { useSpecificationLocationQuantities } from '@/hooks/useSpecificationLocationQuantities';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useAddressValidation } from '@/hooks/useAddressValidation';
import { SingleSpecQuantityEditor, QuantityOption } from '@/components/rfq/SingleSpecQuantityEditor';
import { FinalizedQuantitiesDisplay } from './FinalizedQuantitiesDisplay';
import { SamplesProofsEditor, SampleProofRow } from '@/components/rfq/SamplesProofsEditor';
import { useOrganizationBusinessRules } from '@/hooks/useOrganizationBusinessRules';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';
import { syncSampleDeliveryLocations } from '@/lib/utils/syncSampleDeliveryLocations';

interface RFQTabProps {
  job: Job;
}

export function RFQTab({ job }: RFQTabProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { rules: businessRules } = useOrganizationBusinessRules(user?.organization_id);
  const [loading, setLoading] = useState(false);
  const openRFQFormRef = useRef<HTMLDivElement>(null);
  const criteriaBuilderRef = useRef<PublicRFQCriteriaBuilderRef>(null);
  const [rfqForm, setRfqForm] = useState({
    opens_at: '',
    closes_at: '',
  });
  const [addendumForm, setAddendumForm] = useState({
    note: '',
  });
  
  // RFQ visibility and vendor invitation state
  const [rfqVisibility, setRfqVisibility] = useState<'public' | 'private' | undefined>(undefined);
  const [selectedVendors, setSelectedVendors] = useState<any[]>([]);
  const [shippingTerms, setShippingTerms] = useState<'fob_origin' | 'fob_destination' | null>(null);
  
  // Draft criteria state (before RFQ is opened)
  const [draftPublicCriteria, setDraftPublicCriteria] = useState<Partial<PublicRFQCriteria> | null>(null);
  
  // Track which specification details are expanded
  const [expandedSpecs, setExpandedSpecs] = useState<Set<string>>(new Set());
  const [calculatedPerfectMatchCount, setCalculatedPerfectMatchCount] = useState<number | undefined>(undefined);
  const [calculatedTotalVendorCount, setCalculatedTotalVendorCount] = useState<number | undefined>(undefined);
  
  // Public RFQ criteria hook
  const { perfectMatchCount } = usePublicRFQCriteria(job.rfq?.id);

  // Fetch job specifications
  const { specifications, loading: specsLoading } = useJobSpecifications(job.id);
  
  // Fetch RFQ specification quantities if RFQ exists
  const { quantities, loading: quantitiesLoading } = useRFQSpecificationQuantities(job.rfq?.id || '');

  const [quantityOptions, setQuantityOptions] = useState<Record<string, QuantityOption[]>>({});
  
  // Matrix state for percentage-based distribution
  const { matrix, batchSaveQuantities } = useSpecificationLocationQuantities(job.id);
  const [localMatrix, setLocalMatrix] = useState<any>({});
  
  // Finalized distribution data (after award)
  const [finalizedDistribution, setFinalizedDistribution] = useState<any[]>([]);
  const [isDistributionFinalized, setIsDistributionFinalized] = useState(false);
  
  // Sample requirements state (saved on RFQ open)
  const [sampleRequirements, setSampleRequirements] = useState<SampleProofRow[]>([]);
  
  // Fetch delivery details - now supports multiple locations
  const [deliveryDetails, setDeliveryDetails] = useState<any[]>([]);
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [manualCoords, setManualCoords] = useState<{[key: string]: {lat: string, lng: string}}>({});

  // Check if all quantity options are filled
  const hasAllQuantityOptions = useMemo(() => {
    if (specifications.length === 0) return false;
    return specifications.every(spec => {
      const options = quantityOptions[spec.id];
      return options && options.length > 0 && options.every(opt => opt.quantity > 0 && opt.unit);
    });
  }, [specifications, quantityOptions]);

  // Check if any specification has multiple quantity options (requires percentage distribution)
  const hasMultipleQuantityOptions = useMemo(() => {
    return specifications.some(spec => {
      const options = quantityOptions[spec.id];
      return options && options.length > 1;
    });
  }, [specifications, quantityOptions]);

  // Check if quantity distribution is complete (for multiple locations)
  const hasCompleteQuantityDistribution = useMemo(() => {
    // If only one delivery location, distribution is not needed
    if (deliveryDetails.length <= 1) return true;
    
    // If no specs, can't be complete
    if (specifications.length === 0) return false;
    
    // If multiple quantity options, use percentage-based validation
    if (hasMultipleQuantityOptions) {
      // Check that every specification has percentages totaling 100%
      return specifications.every(spec => {
        if (!localMatrix[spec.id]) return false;
        
        const total = Object.values(localMatrix[spec.id] as Record<string, any>).reduce(
          (sum: number, item: any) => sum + (Number(item.percentage) || 0), 
          0
        );
        
        // Must total 100% (with small tolerance for floating point)
        return Math.abs(total - 100) < 0.01;
      });
    } else {
      // Single quantity option per spec - use direct quantity validation
      return specifications.every(spec => {
        if (!localMatrix[spec.id]) return false;
        
        const options = quantityOptions[spec.id];
        const targetQuantity = options && options.length > 0 ? options[0].quantity : 0;
        
        const total = Object.values(localMatrix[spec.id] as Record<string, any>).reduce(
          (sum: number, item: any) => sum + (Number(item.quantity) || 0), 
          0
        );
        
        return total === targetQuantity;
      });
    }
  }, [specifications, deliveryDetails.length, localMatrix, hasMultipleQuantityOptions, quantityOptions]);

  const fetchDeliveryDetails = async () => {
    try {
      setLoadingDelivery(true);
      const { data, error } = await supabase
        .from('job_delivery_details')
        .select('*')
        .eq('job_id', job.id)
        .order('delivery_order', { ascending: true });

      if (error) throw error;
      setDeliveryDetails(data || []);
    } catch (err) {
      console.error('Error fetching delivery details:', err);
    } finally {
      setLoadingDelivery(false);
    }
  };

  useEffect(() => {
    fetchDeliveryDetails();
  }, [job.id]);

  // Fetch finalized distribution data after award
  useEffect(() => {
    const fetchFinalizedDistribution = async () => {
      if (!['awarded', 'in_progress', 'accepted', 'closed'].includes(job.status)) {
        return;
      }
      
      const { data, error } = await supabase
        .from('job_specification_location_quantities')
        .select('*')
        .eq('job_id', job.id)
        .eq('is_finalized', true);
      
      if (!error && data && data.length > 0) {
        setFinalizedDistribution(data);
        setIsDistributionFinalized(true);
      }
    };
    
    fetchFinalizedDistribution();
  }, [job.id, job.status]);

  // Sync matrix to local state
  useEffect(() => {
    setLocalMatrix(matrix);
  }, [matrix]);

  const handleCriteriaChange = async (criteria: Partial<PublicRFQCriteria>) => {
    setDraftPublicCriteria(criteria);
    
    // Calculate perfect match count preview without saving to DB
    try {
      const { data: countData, error } = await supabase.rpc(
        'calculate_perfect_match_count_preview' as any,
        {
          p_job_id: job.id,
          p_max_distance: null,
          p_required_certs: criteria.required_certification_type_ids,
          p_min_rating: criteria.min_rating,
          p_min_jobs: criteria.min_completed_jobs,
          p_min_verification: criteria.min_verification_level || null
        } as any
      );
      
      if (error) {
        console.error('Error calculating preview:', error);
      } else {
      // Extract both counts from the JSONB response
        setCalculatedPerfectMatchCount(countData?.match_count ?? 0);
        setCalculatedTotalVendorCount(countData?.total_verified_vendors ?? 0);
      }
    } catch (error) {
      console.error('Error calculating preview:', error);
    }
  };

  const handleOpenRFQ = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted, rfqForm:', rfqForm);
    
    if (!rfqForm.closes_at) {
      console.log('Validation failed: closes_at is missing');
      toast({
        title: 'Error',
        description: 'Close date is required',
        variant: 'destructive',
      });
      return;
    }

    if (!rfqVisibility) {
      toast({
        title: 'Error',
        description: 'Please select RFQ visibility (Public or Private)',
        variant: 'destructive',
      });
      return;
    }

    if (!shippingTerms) {
      toast({
        title: 'Error',
        description: 'Shipping terms are required',
        variant: 'destructive',
      });
      return;
    }

    // Validate minimum vendors requirement from organization business rules
    if (businessRules?.minimum_vendors_invited && businessRules.minimum_vendors_invited > 1) {
      if (rfqVisibility === 'private') {
        // For private RFQs: count invited vendors
        if (selectedVendors.length < businessRules.minimum_vendors_invited) {
          toast({
            title: 'Minimum Vendors Required',
            description: `Organization policy requires at least ${businessRules.minimum_vendors_invited} vendors to be invited. Currently ${selectedVendors.length} selected.`,
            variant: 'destructive',
          });
          return;
        }
      } else if (rfqVisibility === 'public') {
        // For public RFQs: check estimated matching vendors
        if ((calculatedPerfectMatchCount || 0) < businessRules.minimum_vendors_invited) {
          toast({
            title: 'Insufficient Eligible Vendors',
            description: `Organization policy requires at least ${businessRules.minimum_vendors_invited} eligible vendors. Currently ${calculatedPerfectMatchCount || 0} vendors match your criteria.`,
            variant: 'destructive',
          });
          return;
        }
      }
    }

    // Validate that all specifications have at least one quantity option
    const missingQuantities = specifications.filter(spec => {
      const options = quantityOptions[spec.id];
      return !options || options.length === 0 || options.some(opt => !opt.quantity || !opt.unit);
    });

    if (missingQuantities.length > 0) {
      toast({
        title: 'Error',
        description: 'All specifications must have at least one valid quantity option (quantity > 0 and unit selected)',
        variant: 'destructive',
      });
      return;
    }

    // Validate distribution if multiple locations
    if (deliveryDetails.length > 1) {
      if (hasMultipleQuantityOptions) {
        // Percentage-based validation
        const allValid = specifications.every(spec => {
          const percentages = Object.values(localMatrix[spec.id] || {}).reduce<number>(
            (sum, item: any) => sum + (Number(item.percentage) || 0),
            0
          );
          return Math.abs(percentages - 100) < 0.01; // Allow for small floating point errors
        });

        if (!allValid) {
          toast({
            title: 'Error',
            description: 'Please distribute percentages correctly. Each specification must have percentages totaling 100%.',
            variant: 'destructive',
          });
          return;
        }
      } else {
        // Direct quantity validation
        const allValid = specifications.every(spec => {
          const options = quantityOptions[spec.id];
          const targetQuantity = options && options.length > 0 ? options[0].quantity : 0;
          
          const total = Object.values(localMatrix[spec.id] || {}).reduce<number>(
            (sum, item: any) => sum + (Number(item.quantity) || 0),
            0
          );
          return total === targetQuantity;
        });

        if (!allValid) {
          toast({
            title: 'Error',
            description: 'Please distribute quantities correctly. Each specification must have quantities matching its target.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    const opensAt = new Date(rfqForm.opens_at || new Date());
    const closesAt = new Date(rfqForm.closes_at);
    const hoursDiff = (closesAt.getTime() - opensAt.getTime()) / (1000 * 60 * 60);

    if (hoursDiff < 24) {
      toast({
        title: 'Error',
        description: 'RFQ must be open for at least 24 hours',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    
    try {
      // Check if RFQ already exists for this job
      const { data: existingRFQ } = await supabase
        .from('rfqs')
        .select('id, status')
        .eq('job_id', job.id)
        .maybeSingle();

      if (existingRFQ) {
        if (existingRFQ.status === 'open') {
          // Check if job status is out of sync (recovery scenario)
          if (job.status === 'draft') {
            // Auto-fix: Update job status to match RFQ status
            const { error: fixError } = await supabase
              .from('jobs')
              .update({ status: 'rfq_open' })
              .eq('id', job.id);
            
            if (!fixError) {
              toast({
                title: 'RFQ Already Open',
                description: 'Found an existing open RFQ. Refreshing the page to show it.',
              });
              window.location.reload();
              return;
            }
          }
          
          toast({
            title: 'RFQ Already Open',
            description: 'This job already has an open RFQ. Please close the existing RFQ before creating a new one.',
            variant: 'destructive',
          });
          setLoading(false);
          return;
        }
        
        // If RFQ exists but is closed/draft, update it
        const { data: rfqData, error: rfqError } = await supabase
          .from('rfqs')
          .update({
            opens_at: rfqForm.opens_at ? new Date(rfqForm.opens_at).toISOString() : new Date().toISOString(),
            closes_at: new Date(rfqForm.closes_at).toISOString(),
            status: 'open',
            visibility_type: rfqVisibility,
            min_vendor_invitations: rfqVisibility === 'private' ? Math.max(3, selectedVendors.length) : null,
            max_vendor_invitations: rfqVisibility === 'private' ? 50 : null,
            shipping_terms: shippingTerms,
          })
          .eq('id', existingRFQ.id)
          .select()
          .single();

        if (rfqError) throw rfqError;

        // Delete old quantity options and invitations for the updated RFQ
        await supabase.from('rfq_specification_quantity_options').delete().eq('rfq_id', existingRFQ.id);
        await supabase.from('rfq_vendor_invitations').delete().eq('rfq_id', existingRFQ.id);

        // Save new quantity options (reusing existing logic below)
        const quantityOptionsToInsert = specifications.flatMap(spec => 
          quantityOptions[spec.id]?.map(opt => ({
            rfq_id: existingRFQ.id,
            specification_id: spec.id,
            quantity_option: opt.quantity,
            unit: opt.unit,
            notes: opt.notes || null
          })) || []
        );

        if (quantityOptionsToInsert.length > 0) {
          const { error: quantityError } = await supabase
            .from('rfq_specification_quantity_options')
            .insert(quantityOptionsToInsert);

          if (quantityError) throw quantityError;
        }

        // Handle distribution for multi-location
        if (deliveryDetails.length > 1) {
          const distributions = [];
          for (const specId in localMatrix) {
            for (const locationId in localMatrix[specId]) {
              const dist = localMatrix[specId][locationId];
              if (hasMultipleQuantityOptions) {
                // Percentage-based distribution
                if (dist.percentage > 0) {
                  distributions.push({
                    job_id: job.id,
                    specification_id: specId,
                    delivery_location_id: locationId,
                    percentage: dist.percentage,
                    notes: dist.notes || null,
                  });
                }
              } else {
                // Direct quantity distribution
                if (dist.quantity > 0) {
                  distributions.push({
                    job_id: job.id,
                    specification_id: specId,
                    delivery_location_id: locationId,
                    quantity: dist.quantity,
                    notes: dist.notes || null,
                  });
                }
              }
            }
          }

          if (distributions.length > 0) {
            const saveResult = await batchSaveQuantities(distributions);
            if (!saveResult.success) {
              throw new Error(`Failed to save distribution: ${saveResult.error || 'Unknown error'}`);
            }
          }
        }

        // Handle invitations for private RFQ
        if (rfqVisibility === 'private' && selectedVendors.length > 0) {
          const invitationData = selectedVendors.map(vendor => ({
            rfq_id: existingRFQ.id,
            vendor_user_id: vendor.user_id,
            invited_by_user_id: user!.id,
            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          }));

          const { error: inviteError } = await supabase
            .from('rfq_vendor_invitations')
            .insert(invitationData);

          if (inviteError) throw inviteError;
        }

        // Save public criteria if applicable
        if (rfqVisibility === 'public') {
          // Always get fresh criteria directly from component (bypass debounce)
          const criteriaToSave = criteriaBuilderRef.current?.getCurrentCriteria();
          
          if (criteriaToSave) {
            console.log('💾 Saving criteria:', criteriaToSave);
            
            const { error: criteriaError } = await supabase
              .from('rfq_targeting_criteria' as any)
              .upsert({
                rfq_id: existingRFQ.id,
                ...criteriaToSave,
                estimated_matching_vendors: calculatedPerfectMatchCount,
                last_match_calculation_at: new Date().toISOString(),
              } as any);

            if (criteriaError) {
              console.error('Error saving criteria:', criteriaError);
              toast({
                title: 'Warning: Criteria Not Saved',
                description: 'Your vendor match criteria could not be saved. The RFQ will be visible to all vendors.',
                variant: 'destructive',
              });
            } else {
              console.log('✅ Criteria saved successfully');
            }
          } else {
            console.log('ℹ️ No criteria set - RFQ will be visible to all vendors');
          }
        }

        // Save sample requirements (delete old ones first for update case)
        await supabase.from('job_sample_requirements').delete().eq('job_id', job.id);
        
        const sampleReqsToInsert = sampleRequirements
          .filter(row => row.item_type && row.specification_id && row.quantity && Number(row.quantity) > 0)
          .map(row => ({
            job_id: job.id,
            specification_id: row.specification_id,
            item_type: row.item_type as 'proofs' | 'samples',
            quantity: Number(row.quantity),
            delivery_template_id: row.delivery_template_id || null,
            attention_of: row.attention_of || null,
            created_by_user_id: user!.id,
          }));

        if (sampleReqsToInsert.length > 0) {
          const { error: sampleError } = await supabase
            .from('job_sample_requirements')
            .insert(sampleReqsToInsert);

          if (sampleError) throw sampleError;

          // Sync sample/proof delivery locations to job_delivery_details
          await syncSampleDeliveryLocations(job.id, sampleReqsToInsert, user!.id);
        }

        // Update job status
        const { error: jobError } = await supabase
          .from('jobs')
          .update({ status: 'rfq_open' })
          .eq('id', job.id);

        if (jobError) throw jobError;

        toast({
          title: 'RFQ Reopened',
          description: 'RFQ has been successfully updated and opened for bidding',
        });

        window.location.reload();
        setLoading(false);
        return;
      }

      // Create new RFQ
      const { data: rfqData, error: rfqError } = await supabase
        .from('rfqs')
        .insert({
          job_id: job.id,
          opens_at: rfqForm.opens_at ? new Date(rfqForm.opens_at).toISOString() : new Date().toISOString(),
          closes_at: new Date(rfqForm.closes_at).toISOString(),
          status: 'open',
          visibility_type: rfqVisibility,
          min_vendor_invitations: rfqVisibility === 'private' ? Math.max(3, selectedVendors.length) : null,
          max_vendor_invitations: rfqVisibility === 'private' ? 50 : null,
          shipping_terms: shippingTerms,
        })
        .select()
        .single();

      if (rfqError) throw rfqError;

      // Save multiple quantity options for each specification
      const quantityOptionsToInsert = specifications.flatMap(spec => 
        quantityOptions[spec.id]?.map(opt => ({
          rfq_id: rfqData.id,
          specification_id: spec.id,
          quantity_option: opt.quantity,
          unit: opt.unit,
          notes: opt.notes || null
        })) || []
      );

      if (quantityOptionsToInsert.length > 0) {
        const { error: quantityError } = await supabase
          .from('rfq_specification_quantity_options')
          .insert(quantityOptionsToInsert);

        if (quantityError) throw quantityError;
      }

      // If multiple locations, save distribution
      // For single location, we skip saving since 100% is automatically inferred
      if (deliveryDetails.length > 1) {
        const distributions = [];
        for (const specId in localMatrix) {
          for (const locationId in localMatrix[specId]) {
            const dist = localMatrix[specId][locationId];
            if (hasMultipleQuantityOptions) {
              // Percentage-based distribution
              if (dist.percentage > 0) {
                distributions.push({
                  job_id: job.id,
                  specification_id: specId,
                  delivery_location_id: locationId,
                  percentage: dist.percentage,
                  notes: dist.notes || null,
                });
              }
            } else {
              // Direct quantity distribution
              if (dist.quantity > 0) {
                distributions.push({
                  job_id: job.id,
                  specification_id: specId,
                  delivery_location_id: locationId,
                  quantity: dist.quantity,
                  notes: dist.notes || null,
                });
              }
            }
          }
        }

        if (distributions.length > 0) {
          const saveResult = await batchSaveQuantities(distributions);
          if (!saveResult.success) {
            throw new Error(`Failed to save distribution: ${saveResult.error || 'Unknown error'}`);
          }
        }
      }
      // Note: For single location RFQs, distribution is not saved to the database
      // The system automatically infers 100% for the single location when needed

      // If private RFQ with selected vendors, create invitations
      if (rfqVisibility === 'private' && selectedVendors.length > 0) {
        const invitationData = selectedVendors.map(vendor => ({
          rfq_id: rfqData.id,
          vendor_user_id: vendor.user_id,
          invited_by_user_id: user!.id,
          expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        }));

        const { error: inviteError } = await supabase
          .from('rfq_vendor_invitations')
          .insert(invitationData);

        if (inviteError) throw inviteError;
      }

      // For public RFQs with criteria, save them
      if (rfqVisibility === 'public') {
        // Always get fresh criteria directly from component (bypass debounce)
        const criteriaToSave = criteriaBuilderRef.current?.getCurrentCriteria();
        
        if (criteriaToSave) {
          console.log('💾 Saving criteria:', criteriaToSave);
          
          const { error: criteriaError } = await supabase
            .from('rfq_targeting_criteria' as any)
            .insert({
              rfq_id: rfqData.id,
              ...criteriaToSave,
              estimated_matching_vendors: calculatedPerfectMatchCount,
              last_match_calculation_at: new Date().toISOString(),
            } as any);

          if (criteriaError) {
            console.error('Error saving criteria:', criteriaError);
            toast({
              title: 'Warning: Criteria Not Saved',
              description: 'Your vendor match criteria could not be saved. The RFQ will be visible to all vendors.',
              variant: 'destructive',
            });
          } else {
            console.log('✅ Criteria saved successfully');
          }
        } else {
          console.log('ℹ️ No criteria set - RFQ will be visible to all vendors');
        }
      }

      // Save sample requirements
      const sampleReqsToInsert = sampleRequirements
        .filter(row => row.item_type && row.specification_id && row.quantity && Number(row.quantity) > 0)
        .map(row => ({
          job_id: job.id,
          specification_id: row.specification_id,
          item_type: row.item_type as 'proofs' | 'samples',
          quantity: Number(row.quantity),
          delivery_template_id: row.delivery_template_id || null,
          attention_of: row.attention_of || null,
          created_by_user_id: user!.id,
        }));

      if (sampleReqsToInsert.length > 0) {
        const { error: sampleError } = await supabase
          .from('job_sample_requirements')
          .insert(sampleReqsToInsert);

        if (sampleError) throw sampleError;

        // Sync sample/proof delivery locations to job_delivery_details
        await syncSampleDeliveryLocations(job.id, sampleReqsToInsert, user!.id);
      }

      // Update job status
      const { error: jobError } = await supabase
        .from('jobs')
        .update({ status: 'rfq_open' })
        .eq('id', job.id);

      if (jobError) throw jobError;

      // Track RFQ published event
      analytics.track({
        name: ANALYTICS_EVENTS.RFQ_PUBLISHED,
        category: 'engagement',
        properties: {
          job_id: job.id,
          category: job.category,
          specification_count: specifications.length,
          freight_terms: shippingTerms,
          visibility: rfqVisibility,
          vendor_count: rfqVisibility === 'private' ? selectedVendors.length : calculatedPerfectMatchCount,
        },
      });

      toast({
        title: 'RFQ Opened',
        description: 'RFQ has been successfully opened for bidding',
      });

      // Refresh the page to show updated data
      window.location.reload();
    } catch (err) {
      console.error('Error opening RFQ:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to open RFQ',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddendum = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!addendumForm.note.trim() || addendumForm.note.length < 10 || addendumForm.note.length > 2000) {
      toast({
        title: 'Error',
        description: 'Note must be between 10 and 2000 characters',
        variant: 'destructive',
      });
      return;
    }

    if (!job.rfq) return;

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const newRevision = job.rfq.revision + 1;

      // Add addendum
      const { error: addendumError } = await supabase
        .from('rfq_addenda')
        .insert({
          rfq_id: job.rfq.id,
          revision: newRevision,
          note: addendumForm.note,
          created_by_user_id: user.id,
        });

      if (addendumError) throw addendumError;

      // Update RFQ revision
      const { error: rfqError } = await supabase
        .from('rfqs')
        .update({ revision: newRevision })
        .eq('id', job.rfq.id);

      if (rfqError) throw rfqError;

      toast({
        title: 'Addendum Added',
        description: `Addendum added to revision ${newRevision}`,
      });

      setAddendumForm({ note: '' });
      // Refresh the page to show updated data
      window.location.reload();
    } catch (err) {
      console.error('Error adding addendum:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to add addendum',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerQuestion = async (e: React.FormEvent, questionId: string) => {
    e.preventDefault();
    
    const form = e.target as HTMLFormElement;
    const answer = (form.querySelector(`#answer-${questionId}`) as HTMLTextAreaElement)?.value?.trim();
    const createsSpecChange = (form.querySelector(`#spec-change-${questionId}`) as HTMLInputElement)?.checked;

    if (!answer) {
      toast({
        title: "Invalid answer",
        description: "Answer cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('rfq_questions')
        .update({
          answer: answer,
          answered_at: new Date().toISOString(),
          answered_by_user_id: user.id,
          creates_spec_change: createsSpecChange || false,
        })
        .eq('id', questionId);

      if (error) throw error;

      toast({
        title: "Answer submitted",
        description: "Your answer has been submitted successfully",
      });

      // Refresh the page to show updated data
      window.location.reload();
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast({
        title: "Error",
        description: "Failed to submit answer",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Legacy geocoding removed - address validation is now handled via Smarty API during address creation

  const handleManualCoordinatesSave = async (deliveryId: string) => {
    const coords = manualCoords[deliveryId];
    if (!coords?.lat || !coords?.lng) return;
    
    const latitude = parseFloat(coords.lat);
    const longitude = parseFloat(coords.lng);
    
    if (isNaN(latitude) || isNaN(longitude)) {
      toast({
        title: 'Invalid Coordinates',
        description: 'Please enter valid numeric coordinates',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      const { error } = await supabase
        .from('job_delivery_details')
        .update({ latitude, longitude })
        .eq('id', deliveryId);
        
      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Coordinates saved successfully',
      });
      
      fetchDeliveryDetails();
      setManualCoords(prev => ({ ...prev, [deliveryId]: { lat: '', lng: '' } }));
    } catch (error) {
      console.error('Error saving coordinates:', error);
      toast({
        title: 'Error',
        description: 'Failed to save coordinates',
        variant: 'destructive'
      });
    }
  };

  // Calculate time remaining only if RFQ exists
  const timeUntilClose = job.rfq ? new Date(job.rfq.closes_at).getTime() - new Date().getTime() : 0;
  const hoursUntilClose = Math.max(0, Math.floor(timeUntilClose / (1000 * 60 * 60)));
  const daysUntilClose = Math.floor(hoursUntilClose / 24);

  const handleScrollToForm = () => {
    openRFQFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="space-y-6">
      {/* Tabbed Content: Specifications & Delivery */}
      <Card>
        <CardHeader>
          <CardTitle>Quantities and Delivery</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="specifications" className="w-full">
            <TabsList className={`grid w-full ${deliveryDetails.length > 1 ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="specifications" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Specifications & Quantities
                {job.status === 'draft' && (specifications.length === 0 || !hasAllQuantityOptions) && (
                  <Badge variant="destructive" className="ml-2">Required</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="delivery" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Delivery Information
                {job.status === 'draft' && !shippingTerms && (
                  <Badge variant="destructive" className="ml-2">Required</Badge>
                )}
              </TabsTrigger>
              {deliveryDetails.length > 1 && (
                <TabsTrigger value="quantities" className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Quantity Distribution
                  {job.status === 'draft' && !hasCompleteQuantityDistribution && (
                    <Badge variant="destructive" className="ml-2">Required</Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>

            <TabsContent value="specifications" className="mt-6 space-y-4">
          {specsLoading ? (
            <div className="text-center py-4">Loading specifications...</div>
          ) : specifications.length > 0 ? (
            <div className="space-y-4">
              {specifications.map((spec) => (
                <div key={spec.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 flex items-center gap-2">
                      <h4 className="font-medium">{spec.specification_name}</h4>
                      {(spec as any).specification_document_id ? (
                        <Badge variant="outline" className="text-xs">
                          <FileText className="h-3 w-3 mr-1" />
                          Document
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          <FileEdit className="h-3 w-3 mr-1" />
                          Form
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Show specification content if available */}
                  {spec.content && typeof spec.content === 'object' && (
                    <Collapsible
                      open={expandedSpecs.has(spec.id)}
                      onOpenChange={(open) => {
                        const newExpanded = new Set(expandedSpecs);
                        if (open) {
                          newExpanded.add(spec.id);
                        } else {
                          newExpanded.delete(spec.id);
                        }
                        setExpandedSpecs(newExpanded);
                      }}
                    >
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-between pt-3 border-t"
                        >
                          <span className="text-sm text-muted-foreground">Specification Details</span>
                          {expandedSpecs.has(spec.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="text-sm bg-muted p-3 rounded mt-2">
                          <SpecificationDataRenderer content={spec.content} specification={spec} />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Inline quantity options - shows for all jobs */}
                  {job.rfq ? (
                    // Show locked quantity options for opened RFQs
                    <div className="pt-3 border-t">
                      <Label className="mb-2 block text-sm font-medium">Quantity Options for RFQ</Label>
                      <div className="space-y-2">
                        {quantities
                          .filter(q => q.specification_id === spec.id)
                          .map((opt, idx) => (
                            <div key={idx} className="grid grid-cols-3 gap-3 p-2 bg-muted/30 rounded">
                              <div>
                                <Label className="text-xs">Quantity</Label>
                                <Input value={opt.quantity_option} disabled className="bg-background" />
                              </div>
                              <div>
                                <Label className="text-xs">Unit</Label>
                                <Input value={opt.unit} disabled className="bg-background" />
                              </div>
                              <div>
                                <Label className="text-xs">Notes</Label>
                                <Input value={opt.notes || 'None'} disabled className="bg-background" />
                              </div>
                            </div>
                          ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Quantity options are locked once RFQ is opened
                      </p>
                    </div>
                  ) : job.status === 'draft' ? (
                    // Show editable quantity options for draft jobs
                    <SingleSpecQuantityEditor
                      specificationId={spec.id}
                      specificationName={spec.specification_name}
                      options={quantityOptions[spec.id] || []}
                      onChange={(options) => {
                        setQuantityOptions({
                          ...quantityOptions,
                          [spec.id]: options
                        });
                      }}
                    />
                  ) : (
                    // Show message for non-draft jobs without RFQ
                    <div className="pt-3 border-t">
                      <p className="text-sm text-muted-foreground">
                        Open RFQ to set quantities for vendor bidding
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No specifications added to this job yet.</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add specifications during job creation to define what vendors should quote.
              </p>
              {job.status === 'draft' && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => navigate(`/jobs/${job.public_id}/edit?tab=specifications`)}
                >
                  <FileEdit className="mr-2 h-4 w-4" />
                  Add Specifications
                </Button>
              )}
            </div>
          )}
            </TabsContent>

            <TabsContent value="delivery" className="mt-6">
              {loadingDelivery ? (
                <div className="text-center py-4">Loading delivery information...</div>
              ) : deliveryDetails.length > 0 ? (
                <div className="space-y-4">
                  {/* Shipping Terms Selector */}
                  {job.status === 'draft' ? (
                    <ShippingTermsSelector 
                      value={shippingTerms} 
                      onChange={setShippingTerms}
                    />
                  ) : job.rfq?.shipping_terms ? (
                    <ShippingTermsSelector 
                      value={job.rfq.shipping_terms as 'fob_origin' | 'fob_destination'} 
                      onChange={() => {}}
                      readOnly
                    />
                  ) : null}

                  {/* Samples and Proofs Section */}
                  {specifications.length > 0 && (
                    <div>
                      <SamplesProofsEditor
                        jobId={job.id}
                        specifications={specifications.map(s => ({ id: s.id, specification_name: s.specification_name }))}
                        readOnly={job.status !== 'draft'}
                        onChange={setSampleRequirements}
                      />
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">
                      {deliveryDetails.length === 1 ? 'Delivery Location' : `Delivery Locations (${deliveryDetails.length})`}
                    </h4>
                  </div>
                  
                  {deliveryDetails.map((delivery, index) => (
                    <Card key={delivery.id} className="border-2">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <MapPin className="h-5 w-5" />
                          {deliveryDetails.length > 1 && <span className="text-muted-foreground">Location {index + 1}:</span>}
                          {delivery.delivery_name}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Delivery Address */}
                            <div className="space-y-3">
                              <h5 className="font-semibold text-sm">Address</h5>
                              <div className="space-y-2 text-sm">
                                <p>{delivery.address_line1}</p>
                                {delivery.address_line2 && <p>{delivery.address_line2}</p>}
                                <p>{delivery.city}, {delivery.state_province} {delivery.postal_code}</p>
                                <p>{delivery.country}</p>
                                {delivery.location_type && (
                                  <Badge variant="outline" className="capitalize mt-2">
                                    {delivery.location_type.replace('_', ' ')}
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Contact Information */}
                            <div className="space-y-3">
                              <h5 className="font-semibold text-sm">Contact Information</h5>
                              <div className="space-y-2 text-sm">
                                {delivery.contact_name && (
                                  <div>
                                    <Label className="text-muted-foreground">Name</Label>
                                    <p>{delivery.contact_name}</p>
                                  </div>
                                )}
                                {delivery.contact_phone && (
                                  <div>
                                    <Label className="text-muted-foreground">Phone</Label>
                                    <p>{delivery.contact_phone}</p>
                                  </div>
                                )}
                                {delivery.contact_email && (
                                  <div>
                                    <Label className="text-muted-foreground">Email</Label>
                                    <p>{delivery.contact_email}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Additional Details */}
                          {(delivery.preferred_delivery_date || (delivery.delivery_window_start && delivery.delivery_window_end)) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                              {delivery.preferred_delivery_date && (
                                <div>
                                  <Label className="text-muted-foreground">Preferred Delivery Date</Label>
                                  <p className="text-sm">{new Date(delivery.preferred_delivery_date).toLocaleString()}</p>
                                </div>
                              )}
                              {delivery.delivery_window_start && delivery.delivery_window_end && (
                                <div>
                                  <Label className="text-muted-foreground">Delivery Window</Label>
                                  <p className="text-sm">
                                    {new Date(delivery.delivery_window_start).toLocaleString()} - {new Date(delivery.delivery_window_end).toLocaleString()}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Requirements & Instructions */}
                          {(delivery.special_instructions || delivery.access_requirements || delivery.dock_information) && (
                            <div className="space-y-3 pt-4 border-t">
                              <h5 className="font-semibold text-sm">Additional Information</h5>
                              {delivery.special_instructions && (
                                <div>
                                  <Label className="text-muted-foreground">Special Instructions</Label>
                                  <p className="text-sm whitespace-pre-wrap">{delivery.special_instructions}</p>
                                </div>
                              )}
                              {delivery.access_requirements && (
                                <div>
                                  <Label className="text-muted-foreground">Access Requirements</Label>
                                  <p className="text-sm whitespace-pre-wrap">{delivery.access_requirements}</p>
                                </div>
                              )}
                              {delivery.dock_information && (
                                <div>
                                  <Label className="text-muted-foreground">Dock Information</Label>
                                  <p className="text-sm whitespace-pre-wrap">{delivery.dock_information}</p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Delivery Hours */}
                          {delivery.delivery_hours && Object.keys(delivery.delivery_hours).length > 0 && (
                            <div className="pt-4 border-t">
                              <Label className="text-muted-foreground">Delivery Hours</Label>
                              <div className="text-sm mt-2">
                                <pre className="whitespace-pre-wrap font-mono text-xs bg-muted p-3 rounded">
                                  {JSON.stringify(delivery.delivery_hours, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}

                          {/* Carriers */}
                          {((delivery.preferred_carriers && delivery.preferred_carriers.length > 0) || 
                            (delivery.restricted_carriers && delivery.restricted_carriers.length > 0)) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                              {delivery.preferred_carriers && delivery.preferred_carriers.length > 0 && (
                                <div>
                                  <Label className="text-muted-foreground">Preferred Carriers</Label>
                                  <ul className="text-sm list-disc list-inside mt-1">
                                    {delivery.preferred_carriers.map((carrier: string, idx: number) => (
                                      <li key={idx}>{carrier}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {delivery.restricted_carriers && delivery.restricted_carriers.length > 0 && (
                                <div>
                                  <Label className="text-muted-foreground">Restricted Carriers</Label>
                                  <ul className="text-sm list-disc list-inside mt-1">
                                    {delivery.restricted_carriers.map((carrier: string, idx: number) => (
                                      <li key={idx}>{carrier}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Delivery Requirements Flags */}
                          {(delivery.signature_required || delivery.id_required || delivery.appointment_required) && (
                            <div className="flex flex-wrap gap-2 pt-4 border-t">
                              {delivery.signature_required && (
                                <Badge variant="outline">Signature Required</Badge>
                              )}
                              {delivery.id_required && (
                                <Badge variant="outline">ID Required</Badge>
                              )}
                              {delivery.appointment_required && (
                                <Badge variant="outline">Appointment Required</Badge>
                              )}
                            </div>
                          )}

                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No delivery information defined for this job.</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Delivery details should be added during job creation.
                  </p>
                  {job.status === 'draft' && (
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => navigate(`/jobs/${job.public_id}/edit?tab=delivery`)}
                    >
                      <FileEdit className="mr-2 h-4 w-4" />
                      Add Delivery Details
                    </Button>
                  )}
                </div>
              )}
            </TabsContent>

            {deliveryDetails.length > 1 && (
              <TabsContent value="quantities" className="mt-6">
                {isDistributionFinalized ? (
                  <FinalizedQuantitiesDisplay
                    specifications={specifications.map(s => ({ id: s.id, specification_name: s.specification_name }))}
                    deliveryLocations={deliveryDetails}
                    distributionData={finalizedDistribution}
                    units={Object.fromEntries(
                      specifications.map(spec => [
                        spec.id,
                        quantityOptions[spec.id]?.[0]?.unit || 'units'
                      ])
                    )}
                  />
                ) : hasMultipleQuantityOptions ? (
                  <QuantitiesMatrix
                    specifications={specifications}
                    deliveryLocations={deliveryDetails}
                    matrix={localMatrix}
                    onPercentageChange={(specId, locationId, percentage) => {
                      setLocalMatrix((prev: any) => {
                        const newMatrix = { ...prev };
                        if (!newMatrix[specId]) {
                          newMatrix[specId] = {};
                        }
                        newMatrix[specId][locationId] = {
                          ...newMatrix[specId][locationId],
                          percentage,
                        };
                        return newMatrix;
                      });
                    }}
                    readOnly={job.status !== 'draft'}
                  />
                ) : (
                  <QuantitiesMatrixDirect
                    specifications={specifications}
                    deliveryLocations={deliveryDetails}
                    quantityOptions={quantityOptions}
                    matrix={localMatrix}
                    onQuantityChange={(specId, locationId, quantity) => {
                      setLocalMatrix((prev: any) => {
                        const newMatrix = { ...prev };
                        if (!newMatrix[specId]) {
                          newMatrix[specId] = {};
                        }
                        newMatrix[specId][locationId] = {
                          ...newMatrix[specId][locationId],
                          quantity,
                        };
                        return newMatrix;
                      });
                    }}
                    readOnly={job.status !== 'draft'}
                  />
                )}
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* RFQ Visibility and Vendor Invitations */}
      {job.status === 'draft' && (
        <>
          <RFQVisibilitySelector
            value={rfqVisibility}
            onChange={setRfqVisibility}
            vendorCount={selectedVendors.length}
          />
          
          {rfqVisibility === 'private' && (
            <VendorInvitationManager
              selectedVendors={selectedVendors}
              onVendorsChange={setSelectedVendors}
              rfqCategory={job.category}
              deliveryLocation={(() => {
                // Find first delivery location with valid coordinates
                const deliveryWithCoords = deliveryDetails.find(
                  d => d.latitude !== null && d.longitude !== null
                );
                return deliveryWithCoords ? {
                  latitude: deliveryWithCoords.latitude,
                  longitude: deliveryWithCoords.longitude
                } : undefined;
              })()}
            />
          )}
          
                {rfqVisibility === 'public' && (
                  <PublicRFQCriteriaBuilder
                    ref={criteriaBuilderRef}
                    rfqId={job.rfq?.id}
                    initialCriteria={job.rfq?.public_rfq_criteria || draftPublicCriteria}
                    onCriteriaChange={handleCriteriaChange}
                    mode={job.rfq?.id ? 'active' : 'draft'}
                    previewMatchCount={calculatedPerfectMatchCount}
                    previewTotalCount={calculatedTotalVendorCount}
                  />
                )}
        </>
      )}

      {/* RFQ Management */}
      <Card ref={openRFQFormRef}>
        <CardHeader>
          <CardTitle>RFQ Management</CardTitle>
        </CardHeader>
        <CardContent>
          {job.status === 'draft' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Open RFQ for Bidding</h3>
              </div>
              
              <form onSubmit={handleOpenRFQ} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="opens_at">Opens At (optional)</Label>
                    <Input
                      id="opens_at"
                      type="datetime-local"
                      value={rfqForm.opens_at}
                      onChange={(e) => setRfqForm({ ...rfqForm, opens_at: e.target.value })}
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Leave empty to open immediately
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="closes_at" className="flex items-center gap-2">
                      Closes At
                      {!rfqForm.closes_at && (
                        <Badge variant="destructive" className="text-xs">Required</Badge>
                      )}
                    </Label>
                    <Input
                      id="closes_at"
                      type="datetime-local"
                      value={rfqForm.closes_at}
                      onChange={(e) => setRfqForm({ ...rfqForm, closes_at: e.target.value })}
                      required
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Must be at least 24 hours from open time
                    </p>
                  </div>
                </div>

                <Button type="submit" disabled={loading}>
                  {loading ? 'Opening RFQ...' : 'Open RFQ for Bidding'}
                </Button>
              </form>
            </div>
          )}

          {job.rfq && (
            <div className="space-y-6">
              {/* RFQ Status - Only show when RFQ is open */}
              {job.status === 'rfq_open' && (
                <div className="bg-muted/30 rounded-lg p-4 border-l-4 border-primary/40">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4" />
                    <h3 className="text-base font-semibold">RFQ Status</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Revision</p>
                      <p className="text-xl font-bold">{job.rfq.revision}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      <Badge className="mt-1">{job.rfq.status}</Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Time Left</p>
                      <p className="text-xl font-bold">
                        {daysUntilClose > 0 ? `${daysUntilClose}d ${hoursUntilClose % 24}h` : `${hoursUntilClose}h`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Opens</p>
                      <p className="text-sm">{job.rfq.opens_at ? new Date(job.rfq.opens_at).toLocaleDateString() : 'Now'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Closes</p>
                      <p className="text-sm">{new Date(job.rfq.closes_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bids Section */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-base font-semibold">Bids</h3>
                </div>
                <BidsTab 
                  rfqId={job.rfq.id} 
                  jobStatus={job.status} 
                  isClient={true}
                  jobId={job.id}
                />
              </div>

              {/* Q&A Section */}
              <div className="space-y-4 pt-6 border-t">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-lg font-semibold">Questions & Answers</h3>
                  <Badge variant="outline">
                    {job.rfq.questions?.length || 0} question(s)
                  </Badge>
                </div>
                {job.rfq.questions && job.rfq.questions.length > 0 ? (
                  <div className="space-y-4">
                    {job.rfq.questions.map((question) => (
                      <div key={question.id} className="border rounded-lg p-4">
                        <div className="mb-2">
                          <p className="font-medium">Q: {question.question}</p>
                          <p className="text-sm text-muted-foreground">
                            Asked on {new Date(question.created_at).toLocaleString()}
                          </p>
                        </div>
                        {question.answer ? (
                          <div className="mt-3 p-3 bg-muted rounded">
                            <p><strong>A:</strong> {question.answer}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              Answered on {question.answered_at ? new Date(question.answered_at).toLocaleString() : 'Unknown'}
                              {question.creates_spec_change && (
                                <Badge variant="outline" className="ml-2">Spec Change</Badge>
                              )}
                            </p>
                          </div>
                        ) : (
                          <div className="mt-3 space-y-3">
                            <p className="text-muted-foreground italic">Awaiting answer...</p>
                            <form 
                              onSubmit={(e) => handleAnswerQuestion(e, question.id)}
                              className="space-y-3 p-3 bg-blue-50 rounded border"
                            >
                              <div>
                                <Label htmlFor={`answer-${question.id}`}>Your Answer</Label>
                                <Textarea
                                  id={`answer-${question.id}`}
                                  placeholder="Type your answer here..."
                                  rows={3}
                                  required
                                />
                              </div>
                              <div className="flex items-center gap-3">
                                <Button type="submit" size="sm" disabled={loading}>
                                  {loading ? 'Submitting...' : 'Submit Answer'}
                                </Button>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id={`spec-change-${question.id}`}
                                    className="rounded"
                                  />
                                  <Label htmlFor={`spec-change-${question.id}`} className="text-sm">
                                    This creates a specification change
                                  </Label>
                                </div>
                              </div>
                            </form>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No questions submitted yet</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Addendum */}
      {job.rfq && job.status === 'rfq_open' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Add Addendum
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddAddendum} className="space-y-4">
              <div>
                <Label htmlFor="addendum_note">Note *</Label>
                <Textarea
                  id="addendum_note"
                  value={addendumForm.note}
                  onChange={(e) => setAddendumForm({ ...addendumForm, note: e.target.value })}
                  placeholder="Enter addendum details..."
                  rows={4}
                />
                <p className="text-sm text-muted-foreground mt-1">
                  {addendumForm.note.length}/2000 characters (minimum 10)
                </p>
              </div>
              
              <Button type="submit" disabled={loading}>
                {loading ? 'Adding...' : 'Add Addendum'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Addenda */}
      {job.rfq?.addenda && job.rfq.addenda.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Addenda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {job.rfq.addenda.map((addendum) => (
                <div key={addendum.id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline">Revision {addendum.revision}</Badge>
                    <p className="text-sm text-muted-foreground">
                      {new Date(addendum.created_at).toLocaleString()}
                    </p>
                  </div>
                  <p className="whitespace-pre-wrap">{addendum.note}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}